import * as go from 'gojs';
import { isEqual } from 'vet-bones/utils';

import {
  NODE_SIZE,
  SELF_LINK_CURVINESS,
} from 'src/containers/ProjectDiagram/constants/styles';
import {
  ExtendedLayeredDigraphLayout,
  computeLinkDirectionInfo,
} from 'src/containers/ProjectDiagram/templates/extensions/layouts/LayeredLayout/ExtendedLayeredDigraphLayout';
import { LayeredLinkSettings } from 'src/containers/ProjectDiagram/templates/extensions/layouts/types';
import { LinkSelfLoopProperties } from 'src/containers/ProjectDiagram/types/link';

type DirectedLinkSettings = LayeredLinkSettings & {
  layoutIsHorizontal: boolean;
  isForward: boolean;
};

export class ExtendedLink extends go.Link {
  private _forceNewPosition = false;

  /**
   * Gets or sets whether this part should be positioned during a layout.
   * The initial and normal value is false.
   *
   * This differs from #isLayoutPositioned in that setting this value to `true`
   * allows this part to still be considered in determining the layout of other
   * parts, but this part itself will not be not repositioned.
   */
  get forceNewPosition() {
    return this._forceNewPosition;
  }

  set forceNewPosition(value: boolean) {
    this._forceNewPosition = value;
  }

  private _isNewlyAdded = true;

  /**
   * Gets or sets whether this link has just been created, and not yet had a
   * layout applied to it. The initial value is true, and should remain false
   * after the first PARTIAL or FULL layout has been applied.
   *
   * This ensures that go.js initially applies a shape that is consistent with
   * non-circular bounding boxes, as well as consistent with subsequent
   * interactions
   */
  get isNewlyAdded() {
    return this._isNewlyAdded;
  }

  set isNewlyAdded(value: boolean) {
    this._isNewlyAdded = value;
  }

  cloneProtected(copy: this) {
    super.cloneProtected(copy);
    // eslint-disable-next-line no-param-reassign
    copy.forceNewPosition = this.forceNewPosition;
    // eslint-disable-next-line no-param-reassign
    copy.isNewlyAdded = this.isNewlyAdded;
  }

  updateParallelPorts() {
    if (
      this.diagram?.layout instanceof ExtendedLayeredDigraphLayout &&
      this.key !== undefined &&
      this.fromNode?.key !== undefined &&
      this.toNode?.key !== undefined
    ) {
      const layout = this.diagram.layout as ExtendedLayeredDigraphLayout;
      // only need to update port orders for to / from nodes if our
      // pseudoAngle, relative to either node, has changed
      const cachedFromInfo = layout.pseudoAngles[this.fromNode.key]?.[this.key];
      const cachedToInfo = layout.pseudoAngles[this.toNode.key]?.[this.key];
      if (
        !isEqual(
          cachedFromInfo,
          computeLinkDirectionInfo(
            this,
            this.fromNode,
            layout.layoutIsHorizontal
          )
        ) ||
        !isEqual(
          cachedToInfo,
          computeLinkDirectionInfo(this, this.toNode, layout.layoutIsHorizontal)
        )
      ) {
        layout.updatePortOrdersForNode(this.fromNode);
        layout.updatePortOrdersForNode(this.toNode);
      }
    }
  }

  getDirectedLinkSettings(): DirectedLinkSettings | null {
    if (
      this.diagram?.layout instanceof ExtendedLayeredDigraphLayout &&
      this.fromNode?.position?.isReal() &&
      this.toNode?.position?.isReal()
    ) {
      const { layoutIsHorizontal } = this.diagram.layout;
      const isForward = layoutIsHorizontal
        ? this.fromNode.position.x < this.toNode.position.x
        : this.fromNode?.position.y < this.toNode?.position.y;
      return {
        ...(isForward
          ? this.diagram.layout.layoutSettings.forwardLinkSettings
          : this.diagram.layout.layoutSettings.backwardLinkSettings),
        layoutIsHorizontal,
        isForward,
      };
    }
    return null;
  }

  computeThickness() {
    if (this.data.labelKeys.length) {
      return super.computeThickness();
    }
    return 100;
  }

  get isAvoiding(): boolean {
    const directedSettings = this.getDirectedLinkSettings();
    if (!directedSettings) {
      return super.isAvoiding;
    }
    return directedSettings.routing === go.Routing.AvoidsNodes;
  }

  computeEndSegmentLength(
    node: go.Node | null,
    port: go.GraphObject | null,
    spot: go.Spot,
    from: boolean
  ): number {
    const directedSettings = this.getDirectedLinkSettings();
    if (!directedSettings) {
      return super.computeEndSegmentLength(node, port, spot, from);
    }
    return from
      ? directedSettings.fromEndSegmentLength
      : directedSettings.toEndSegmentLength;
  }

  computeSelfLoopProperties(): LinkSelfLoopProperties | null {
    if (
      !this.data ||
      !this.fromNode ||
      !this.toNode ||
      this.fromNode.key !== this.toNode.key
    ) {
      return null;
    }

    // Compute a stable (arbitrary) index number based on the order of self link keys
    const allSiblingSelfLinks: string[] = [];
    this.fromNode.linksConnected.each((link) => {
      if (
        link.fromNode &&
        link.toNode &&
        link.fromNode.key === link.toNode.key
      ) {
        allSiblingSelfLinks.push(link.key as string);
      }
    });
    const loopIndex = allSiblingSelfLinks.sort().indexOf(this.key as string);
    // Stagger arcs in fixed positions on two sides of the node,
    // increasing the radius when there are more than two self-loops
    const alternateSide = loopIndex % 2 === 1;
    const stackDepth = Math.floor(loopIndex / 2);

    return {
      alternateSide,
      stackDepth,
    };
  }

  computeCurviness() {
    const selfLoopProperties = this.computeSelfLoopProperties();
    if (selfLoopProperties) {
      const nodeSize = this.fromNode?.actualBounds.size.width || NODE_SIZE;
      const safeNodeSize = Number.isNaN(nodeSize) ? NODE_SIZE : nodeSize;
      const curviness =
        SELF_LINK_CURVINESS *
        safeNodeSize *
        (1 + selfLoopProperties.stackDepth);
      return Number.isNaN(curviness) ? 0 : curviness;
    }

    const directedSettings = this.getDirectedLinkSettings();
    if (directedSettings) {
      const { curviness } = directedSettings;
      return Number.isNaN(curviness) ? 0 : curviness;
    }
    return super.computeCurviness();
  }

  computeSpot(from: boolean, port?: go.GraphObject): go.Spot {
    const selfLoopProperties = this.computeSelfLoopProperties();
    if (selfLoopProperties) {
      if (selfLoopProperties.alternateSide) {
        if (from) {
          return go.Spot.Bottom;
        }
        return go.Spot.Left;
      }
      if (from) {
        return go.Spot.Top;
      }
      return go.Spot.Right;
    }
    const layout = this.diagram?.layout as ExtendedLayeredDigraphLayout;
    const directedSettings = this.getDirectedLinkSettings();
    if (
      directedSettings &&
      this.key !== undefined &&
      this.fromNode?.key !== undefined &&
      this.toNode?.key !== undefined &&
      layout.setsPortSpots === false
    ) {
      this.updateParallelPorts();
      const port = from
        ? layout.parallelPorts[this.fromNode.key]?.[this.key]
        : layout.parallelPorts[this.toNode.key]?.[this.key];
      if (port !== undefined) {
        return port;
      }
    }
    return super.computeSpot(from, port);
  }

  makeGeometry(): go.Geometry {
    // Safety check to prevent NaN in geometry calculations

    const { fromNode, toNode } = this;

    if (
      fromNode &&
      toNode &&
      (!fromNode?.position?.isReal() || !toNode?.position?.isReal())
    ) {
      // Return a simple line geometry as fallback
      const geometry = new go.Geometry(go.GeometryType.Line);
      geometry.startX = 0;
      geometry.startY = 0;
      geometry.endX = 100;
      geometry.endY = 0;
      return geometry;
    }

    return super.makeGeometry();
  }
}
